export class ZvukError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'ZvukError';
  }
}

export class EngineClosedError extends ZvukError {
  constructor() {
    super('Engine is closed. Create a new engine with createEngine().');
    this.name = 'EngineClosedError';
  }
}

/** `did you mean "x"?`, or nothing when no candidate was close enough. */
function hintOf(hint: string | null | undefined): string {
  return hint ? ` Did you mean "${hint}"?` : '';
}

export class BusNotFoundError extends ZvukError {
  constructor(name: string, hint?: string | null) {
    super(`Bus "${name}" is not configured. Declare it in createEngine({ buses: { ... } }).${hintOf(hint)}`);
    this.name = 'BusNotFoundError';
  }
}

export class SoundNotFoundError extends ZvukError {
  constructor(name: string, hint?: string | null) {
    super(
      `Sound "${name}" is not loaded. Call engine.loadSound() or load a bank that contains it.${hintOf(hint)}`,
    );
    this.name = 'SoundNotFoundError';
  }
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * A fetch or decode failure for one URL. The underlying failure is kept on
 * `cause` as well as summarised into the message, so a logger that walks
 * the cause chain gets the original stack.
 */
export class DecodeError extends ZvukError {
  constructor(url: string, cause: unknown, message?: string) {
    super(message ?? `Failed to decode audio at "${url}": ${describe(cause)}`, { cause });
    this.name = 'DecodeError';
  }
}

export interface DecodeAttempt {
  readonly url: string;
  readonly cause: unknown;
}

export interface PreloadFailure {
  readonly name: string;
  readonly cause: unknown;
}

/**
 * Thrown by `engine.preload(...)` when one or more items in the batch fail.
 * Other items in the batch still complete; this only fires after every item
 * has settled, so a single broken asset doesn't short-circuit the rest of a
 * loading screen.
 */
export class PreloadError extends ZvukError {
  readonly failures: readonly PreloadFailure[];
  constructor(failures: readonly PreloadFailure[]) {
    const summary = failures.map((f) => `  - ${f.name}: ${describe(f.cause)}`).join('\n');
    super(`Failed to preload ${failures.length} item(s):\n${summary}`, {
      cause: failures[0]?.cause,
    });
    this.name = 'PreloadError';
    this.failures = failures;
  }
}

/**
 * Thrown when every URL in a fallback list fails to load. Subclass of
 * DecodeError so existing `catch (e instanceof DecodeError)` paths still
 * fire — `attempts` exposes the per-URL causes for diagnostics.
 */
export class AggregateDecodeError extends DecodeError {
  readonly attempts: readonly DecodeAttempt[];
  constructor(urls: readonly string[], attempts: readonly DecodeAttempt[]) {
    const last = attempts[attempts.length - 1];
    const summary = attempts.map((a) => `  - ${a.url}: ${describe(a.cause)}`).join('\n');
    // Built before super() rather than reassigned after it: rewriting
    // this.message afterwards left `stack` carrying the old text.
    super(
      urls[urls.length - 1] ?? '<empty>',
      last?.cause,
      `Failed to load any of ${urls.length} fallback URLs:\n${summary}`,
    );
    this.name = 'AggregateDecodeError';
    this.attempts = attempts;
  }
}
